/**
 * CycloneTracker.tsx — MapLibre version
 * مراقب الأعاصير — بدون Leaflet، يستخدم DOM/SVG مباشرة مع MapLibre.
 *
 * التصميم مبني على قواعد الأرصاد الحقيقية (لا زخرفة عشوائية): تدرّج ألوان
 * سافير-سيمبسون المعياري، خطّ مسار متصل يتحوّل من مصمت (المسافة المقطوعة فعلاً) إلى
 * متقطّع (التوقّع القادم)، و"مخروط عدم اليقين" الذي يتّسع كلما بعُد التوقّع زمنياً —
 * نفس اللغة البصرية التي يعرضها Zoom Earth وWindy ومركز الأعاصير الوطني الأمريكي.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMapRef } from './MapContext';
import { cameraKey } from './mapCameraKey';
import { useShallow } from 'zustand/react/shallow';
import { useWeatherStore } from '../../store/weatherStore';
import { useTranslation } from '../../i18n/LanguageContext';
// WiBarometer/WiStrongWind سقطتا مع بطاقتَي الإحصاء: التذييل صار نصّياً بوحداته
// الصريحة (hPa / km/h) فلا يحتاج أيقونة تشرح ما تشرحه الوحدة نفسها.
import { WiDirectionUp } from 'react-icons/wi';
import { FiChevronRight } from 'react-icons/fi';
import { getActiveCyclones, getInitialActiveCyclones, getDisturbances, type Cyclone, type Disturbance } from '../../services/cycloneService';

/* ── فصل لونَي "البيانات" و"الواجهة" ──────────────────────────────────────────
   جُرِّب سلّم أزرق كامل مشتقّ من هوية الموقع فأخفق وظيفياً: TS و1 كانا أزرقين شاحبين
   شبه متطابقين، ومعظم العواصف تعيش عند هذين الطرفين بالضبط — فبدت كل نقاط المسار
   بلون واحد ولم يُقرأ تصاعد الشدّة إطلاقاً. لون واحد لا يحمل ستّ درجات مميّزة.
   الحلّ فصل الدورين:
     · categoryColor  = طيف سافير-سيمبسون القياسي (أخضر→أصفر→برتقالي→أحمر→بنفسجي)،
       ويصبغ البيانات وحدها: نقاط المسار، خطوطه، المخروط، شارات الفئة. مطابق لِـ
       Zoom Earth/NHC، والتمييز بين الفئات فوري.
     · DOCK_ACCENT = أزرق مبدّل النماذج (#6d5efc) ويصبغ كروم اللوحة وحده: القرص،
       الرقم، توهّج السلّم. فتبقى الواجهة منسجمة مع الموقع بلا أحمر يغزوها. */
/* ── سلّم الشدّة ───────────────────────────────────────────────────────────────
   عاد إلى سافير–سيمبسون الكامل بعد أن كان لونين. السبب طلب صريح بمطابقة Windy:
   دليل رموز يعرض عشر درجات لا يستطيع الاستناد إلى لونين، والخريطة يجب أن تطابق
   دليلها وإلّا صار الدليل كاذباً.
   شكل الأيقونة نفسه لم يتغيّر: كل أيقونات العائلة مولَّدة من أصل Figma الواحد
   بإعادة تلوين لا بإعادة رسم (راجع scripts/build_cyclone_icons.py)، والرقم مكتوب
   داخل عين الإعصار — وهي مساحة فارغة في التصميم الأصلي — كما يفعل Windy. */
export interface CycLevel {
    key: string;
    label: string;
    color: string;
    /** حدّ الرياح الأدنى بالكيلومتر/ساعة (سافير–سيمبسون). null ⇒ لا حدّ رقمي. */
    minWind: number | null;
    maxWind: number | null;
}

export const CYC_LEVELS: CycLevel[] = [
    /* الألوان منقولة حرفياً من ملفّ أنماط zoom.earth (قواعد .tooltip.storm.rank*)،
       لا مختارة ذوقاً — وهو المرجع المطلوب مطابقته. يجب أن تبقى مطابقة لما في
       scripts/build_cyclone_icons.py، فالأيقونات تُولَّد من تلك القائمة.

       تخرج درجتان عن المرجع بطلبٍ صريح:
       · cat-5: كان ورديّ zoom.earth (‏#ff55bb) وقُرئ باهتاً لا يوحي بالأقصى. صار
         أحمر قانياً داكناً (‏#b80026) — أغمق من أحمر الفئة الرابعة لا مطابقاً له،
         فيبقى التدرّج تصاعدياً مقروءاً ولا تلتبس الدرجتان.
       · storm: كان أخضر نقياً (‏#00f060). صار مائلاً للفيروزي (‏#00e0a0) — يبقى
         بينه وبين أزرق المنخفض المداري تحته (‏#0a84ff) فرقٌ واضح. */
    { key: 'cat-5',       label: '5',  color: '#b80026', minWind: 252, maxWind: null },
    { key: 'cat-4',       label: '4',  color: '#ff0022', minWind: 209, maxWind: 251 },
    { key: 'cat-3',       label: '3',  color: '#ff5900', minWind: 178, maxWind: 208 },
    { key: 'cat-2',       label: '2',  color: '#ff9400', minWind: 154, maxWind: 177 },
    { key: 'cat-1',       label: '1',  color: '#ffcc00', minWind: 119, maxWind: 153 },
    { key: 'storm',       label: 'TS', color: '#00e0a0', minWind: 63,  maxWind: 118 },
    { key: 'depression',  label: 'TD', color: '#0a84ff', minWind: 0,   maxWind: 62 },
    { key: 'disturbance', label: 'D',  color: '#7722ff', minWind: null, maxWind: null },
    { key: 'post',        label: 'PT', color: '#8f9bb3', minWind: null, maxWind: null },
    { key: 'remnant',     label: 'RL', color: '#6b7688', minWind: null, maxWind: null },
];


/**
 * سقف سلّم الشدّة (كم/س) لرسم أشرطة المفتاح.
 *
 * الفئة الخامسة مفتوحة الأعلى (‏≥252)، فلا حدّ طبيعي يُقاس عليه. تأخذ 100% صراحةً،
 * ويُقاس الباقي على سقفٍ أعلى قليلاً من عتبتها.
 *
 * ولماذا 280 لا 252: بالسقف 252 يصير حدّ الفئة الرابعة (‏251) نسبةَ 99.6% فيُقرَّب
 * إلى 100 — أي شريطان متطابقان لدرجتين مختلفتين، وهو نقضٌ للغرض من المقياس. الهامش
 * يفصلهما (‏90% مقابل 100%) مع بقاء الترتيب والنِّسَب صادقة.
 */
const CYC_SCALE_MAX = 280;

/** مفتاح الدرجة من رقم الفئة (0 = عاصفة مدارية فما دون). */
function levelKey(cat: number): string {
    if (cat >= 5) return 'cat-5';
    if (cat >= 4) return 'cat-4';
    if (cat >= 3) return 'cat-3';
    if (cat >= 2) return 'cat-2';
    if (cat >= 1) return 'cat-1';
    // الاصطلاح من categoryFromWind: 0 عاصفة مدارية، ‎-1 منخفض مداري. كان كل ما دون
    // الفئة 1 يُعرَض «عاصفة مدارية»، فبدا منخفض بسرعة 55 كم/س عاصفةً بينما تسمّيه
    // NHC وZoom Earth منخفضاً (D) — وأيقونة المنخفض ومدخلها في الدليل موجودان
    // أصلاً ولم يكن شيء يصل إليهما.
    if (cat >= 0) return 'storm';
    return 'depression';
}

/** الرمز المختصر المعروض داخل الشارات (5..1 / TS / TD …) — من نفس القائمة. */
function levelBadge(cat: number): string {
    return CYC_LEVELS.find((l) => l.key === levelKey(cat))?.label ?? 'TS';
}

/** مفتاح الترجمة لاسم الدرجة — يوحّد كل نصوص التصنيف على سلّم واحد. */
function levelNameKey(cat: number): string {
    return `cyclone.level.${levelKey(cat)}`;
}

export function levelIcon(key: string): string {
    return `/icons/cyclone/gen/${key}.png`;
}

function categoryColor(cat: number): string {
    const k = levelKey(cat);
    return CYC_LEVELS.find((l) => l.key === k)?.color ?? '#00e0a0';
}

function categoryIcon(cat: number): string {
    return levelIcon(levelKey(cat));
}

/** لون الاضطراب المداري — من السلّم الموحّد كي لا يفترق التلميح عن الأيقونة. */
const DISTURBANCE_COLOR = CYC_LEVELS.find((l) => l.key === 'disturbance')!.color;

/** لون كروم اللوحة — ثابت بهوية الموقع، لا يتبع فئة العاصفة. */
const DOCK_ACCENT = '#6d5efc';

/** اتجاه البوصلة (8 قطاعات) من درجة — لعرض "شمال غربي" بدل رقم مجرّد. */
const COMPASS_KEYS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
function compassKey(deg: number): typeof COMPASS_KEYS[number] {
    const i = Math.round(((deg % 360) + 360) % 360 / 45) % 8;
    return COMPASS_KEYS[i];
}

/* ── التاريخ والوقت من قاموس الموقع لا من locale الجهاز ────────────────────────
   استُعمل هنا سابقاً `toLocale*(undefined, …)` فورث لغة نظام التشغيل: زائرٌ عربي
   الجهاز يقرأ «20 أغسطس» و«ص/م» داخل واجهة إنجليزية، وبخطّ احتياطي غير خطّ الموقع
   لأن السلسلة نفسها صارت عربية. فالتنسيق الآن يمرّ بـ`t` مثل الشريط الزمني تماماً،
   فيتبع مبدّل اللغة وحده — نصّاً وخطّاً. */
type TFn = (key: string, params?: Record<string, string | number>) => string;

/** «20 Aug» / «20 أغسطس» — الشهر المختصر لعمود الجدول الضيّق. */
function formatTrackDate(d: Date, t: TFn): string {
    return `${d.getDate()} ${t(`monthShort.${d.getMonth() + 1}`)}`;
}

/** «03:02 PM» / «03:02 م» — 12 ساعة بلاحقة من القاموس. */
function formatTrackTime(d: Date, t: TFn): string {
    const h = d.getHours();
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${String(h12).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} ${t(h < 12 ? 'time.am' : 'time.pm')}`;
}

/** «Thursday 03:02 PM» / «الخميس 03:02 م» — سطر التلميح فوق نقطة المسار. */
function formatTipWhen(d: Date, t: TFn): string {
    return `${t(`day.${d.getDay()}`)} ${formatTrackTime(d, t)}`;
}

/**
 * يحوّل وقت نقطة المسار إلى إزاحة بالساعات عن "الآن":
 * - 'now' → 0
 * - '+6h' / '+24h' (صيغة NHC النسبية في الباك اند) → 6 / 24
 * - سلسلة زمنية مطلقة (ISO) → الفارق بالساعات عن اللحظة الحالية
 * - غير ذلك (مسارات GDACS بلا أوقات → '') → null (لا تُستخدَم في الاستيفاء)
 */
function trackHourOffset(time: string): number | null {
    if (!time) return null;
    if (time === 'now') return 0;
    const m = /^\s*\+?\s*(-?\d+(?:\.\d+)?)\s*h\s*$/i.exec(time);
    if (m) return parseFloat(m[1]);
    const t = Date.parse(time);
    if (Number.isFinite(t)) return (t - Date.now()) / 3_600_000;
    return null;
}

interface TrackPt { o: number; lat: number; lon: number; cat: number; wind: number }

/**
 * تسمية زمنية مضغوطة لبطاقة المسار. الباك اند صار يرسل ISO مطلقاً (لا "+12h") كي لا
 * ينحرف التوقيت مع تقادم النشرة بين تحديثين — لكن طباعته حرفياً تُخرج
 * "2026-08-15T18:00:00+00:00" الذي يفيض من بطاقة عرضها 66px. نحوّله هنا إلى الأفق
 * النسبي (+12h) وهو ما يهمّ فعلاً في مسار توقّع، ونترك التوقيت المطلق في title.
 */
function trackTimeLabel(time: string, nowText: string): string {
    if (time === 'now') return nowText;
    const o = trackHourOffset(time);
    if (o === null) return time;
    const h = Math.round(o);
    if (Math.abs(h) < 1) return nowText;
    // U+2212 (سالب حقيقي) لا شرطة: يبقى الرقم مقروءاً في سياق RTL بلا التباس بالواصلة.
    return `${h > 0 ? '+' : '−'}${Math.abs(h)}h`;
}

/**
 * وكالة المسار، مشتقّة من بادئة المعرّف الرسمية (AL/EP/CP) — وهي البادئة نفسها التي
 * يبني عليها Windy تسمية "NHC-CP". المعرّفات المسبوقة بـgdacs- تأتي من موجز GDACS
 * العالمي لا من NHC. نُصرّح بالمصدر لأن مسارَي وكالتين قد يختلفان للعاصفة نفسها،
 * فإخفاء المصدر يجعل الرقم بلا مرجع يُحاكَم عليه.
 */
function trackSource(id: string): string {
    if (id.startsWith('gdacs-')) return 'GDACS';
    return match2(id.slice(0, 2).toUpperCase());
}

function match2(prefix: string): string {
    if (prefix === 'AL') return 'NHC-AT';
    if (prefix === 'EP') return 'NHC-EP';
    if (prefix === 'CP') return 'NHC-CP';
    return 'NHC';
}

/** إحداثية بنصف الكرة (18.2°N) لا برقم سالب (‎-18.2°) — الاصطلاح الملاحي المعتاد. */
function formatCoord(value: number, positive: string, negative: string): string {
    return `${Math.abs(value).toFixed(1)}°${value >= 0 ? positive : negative}`;
}

/** نقاط المسار مرتّبة زمنياً (بلا نقاط الوقت المجهول التي لا تصلح للاستيفاء/الرسم الزمني). */
function orderedTrack(c: Cyclone): TrackPt[] {
    const pts: TrackPt[] = [];
    for (const p of c.track) {
        const o = trackHourOffset(p.time);
        if (o !== null && Number.isFinite(p.lat) && Number.isFinite(p.lon)) {
            pts.push({ o, lat: p.lat, lon: p.lon, cat: p.cat, wind: p.wind });
        }
    }
    return pts.sort((a, b) => a.o - b.o);
}

/**
 * موضع مركز الإعصار عند لحظة الخط الزمني (hoursFromNow بالساعات عن الآن) باستيفاء خطّي
 * على نقاط مساره ذات الأوقات المعروفة. هذا ما يجعل الأيقونة **تلاحق** الإعصار أثناء
 * التشغيل بدل بقائها مثبّتة عند موضع "الآن" بينما يتحرّك حقل الرياح للتوقّع.
 * إن كان للمسار أقلّ من نقطتين بأوقات صالحة (أعاصير GDACS بلا أوقات) نُبقيه ثابتاً.
 */
function cyclonePositionAt(
    pts: TrackPt[], c: Cyclone, hoursFromNow: number,
): { lon: number; lat: number; cat: number; wind: number } {
    if (pts.length < 2) return { lon: c.lon, lat: c.lat, cat: c.category, wind: c.maxWind };
    if (hoursFromNow <= pts[0].o) return pts[0];
    const last = pts[pts.length - 1];
    if (hoursFromNow >= last.o) return last;
    for (let i = 1; i < pts.length; i++) {
        if (hoursFromNow <= pts[i].o) {
            const a = pts[i - 1], b = pts[i];
            const span = b.o - a.o;
            const t = span > 0 ? (hoursFromNow - a.o) / span : 0;
            // الموضع يستوفى بسلاسة، أما الفئة فتتبع آخر نشرة صالحة إلى أن تصل
            // النقطة التالية؛ بذلك يطابق لون البؤرة لون القطاع الذي تسير عليه.
            //
            // خط الطول يُستوفى **بعد الفكّ**: نقطتان على جانبَي خطّ التاريخ (‎179 و‎-179)
            // متجاورتان على الأرض بدرجتين، لكن الاستيفاء الخام بينهما يقطع العالم كلّه
            // في الاتجاه المعاكس — فتنزلق العاصفة عبر الخريطة عند مواضع الشريط الزمني
            // الواقعة داخل ذلك الضلع، ويُشدّ معها مسارها ومخروطها من حافّة إلى حافّة
            // (كل هندسة الإعصار تُفكّ حول هذا الموضع، فخطؤه يمتدّ إليها كلّها).
            const bLon = unwrapLon(b.lon, a.lon);
            return { lon: a.lon + (bLon - a.lon) * t, lat: a.lat + (b.lat - a.lat) * t, cat: a.cat, wind: a.wind };
        }
    }
    return last;
}

/**
 * يُرجع خط طول إلى أقرب نسخة له من مرجعٍ معلوم (±360).
 *
 * ضروريّ لكل ما يُرسم بالخطوط لا بالنقاط. الخريطة أسطوانية: خط الطول ‎179.9 و‎-179.9
 * متجاوران على الأرض لكن يقعان على طرفَي الصورة المسطّحة. فالضلع بينهما يُرسم خطّاً
 * يعبر الخريطة بعرضها كاملاً — وهو ما ظهر فعلاً في مسار إعصار لالا بعد أن عبر خطّ
 * التاريخ الدولي.
 *
 * المرجع هو موضع العاصفة الحالي: كل نقاط مسارها ومخروطها تُنقل إلى نسخة العالم التي
 * تسكنها العاصفة نفسها، فيبقى الشكل ملتحماً وفوق علامتها لا في نسخة أخرى.
 *
 * النقاط المفردة (العلامات) لا تحتاجه: لا ضلع بينها يُمَدّ، وMapLibre يضعها في أقرب
 * نسخة تلقائياً.
 */
function unwrapLon(lon: number, reference: number): number {
    let out = lon;
    while (out - reference > 180) out -= 360;
    while (reference - out > 180) out += 360;
    return out;
}

/** يمنع وصلاً وهمياً بين نقطتين متباعدتين بصورة مستحيلة في نشرات الإعصار. */
function isPlausibleTrackSegment(a: TrackPt, b: TrackPt): boolean {
    const hours = b.o - a.o;
    if (!Number.isFinite(hours) || hours <= 0) return false;
    const lat1 = a.lat * Math.PI / 180;
    const lat2 = b.lat * Math.PI / 180;
    const dLat = lat2 - lat1;
    const dLon = (unwrapLon(b.lon, a.lon) - a.lon) * Math.PI / 180;
    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    const km = 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return km <= Math.max(900, hours * 180 + 300);
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * تمرير أفقي بثلاث وسائل على شريط المسار، بدل الاعتماد على شريط التمرير وحده:
 *   · اللمس — متروك لتمرير المتصفّح الأصلي عمداً (زخم النظام وارتداده لا يُحاكيان بجودة).
 *   · سحب الماوس — Pointer Events مع setPointerCapture كي يستمرّ السحب لو خرج المؤشّر.
 *   · عجلة الماوس — الدوران الرأسي (deltaY) يُترجَم تمريراً أفقياً، وهو ما لا يفعله
 *     المتصفّح تلقائياً فوق حاوية أفقية.
 *
 * عند بلوغ أي طرف لا نبتلع حدث العجلة: يعود التمرير لصفحة/لوحة الأب طبيعياً بدل أن
 * يبدو المؤشّر عالقاً. ولأن اتجاه scrollLeft ينعكس في RTL، تُحسب الحدود من اتجاه
 * العنصر الفعلي لا بافتراض LTR.
 */
function useHorizontalDragScroll() {
    // callback ref بحالة (لا useRef): يُعاد تشغيل الـeffect عند تركيب/إزالة العنصر فعلياً،
    // فلا يبقى مستمع معلّقاً على عقدة قديمة عند تبديل الإعصار المختار.
    const [el, setEl] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!el) return;

        const isRtl = () => getComputedStyle(el).direction === 'rtl';

        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey) return; // تكبير الصفحة — ليس تمريراً
            const raw = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
            if (!raw) return;
            const span = el.scrollWidth - el.clientWidth;
            if (span <= 0) return;
            const rtl = isRtl();
            // في RTL يتقدّم الشريط يساراً، أي نحو scrollLeft أصغر.
            const step = rtl ? -raw : raw;
            const lo = rtl ? -span : 0;
            const hi = rtl ? 0 : span;
            const next = Math.max(lo, Math.min(hi, el.scrollLeft + step));
            if (next === el.scrollLeft) return; // عند الطرف: اترك الحدث لأب العنصر
            e.preventDefault();
            el.scrollLeft = next;
        };

        // عتبة قبل اعتبار الحركة سحباً. بدونها كان setPointerCapture يُستدعى عند أي
        // pointerdown فيُعيد توجيه المؤشّر إلى الحاوية، فلا يُولّد المتصفّح حدث click على
        // البطاقة الابنة إطلاقاً — أي أن الضغط على أي توقيت كان لا يفعل شيئاً.
        const DRAG_THRESHOLD_PX = 4;
        let startX = 0, startLeft = 0, pending = false, dragging = false;

        const onPointerDown = (e: PointerEvent) => {
            // اللمس له تمريره الأصلي، والأزرار غير اليسرى ليست سحباً.
            if (e.pointerType === 'touch' || e.button !== 0) return;
            pending = true;
            dragging = false;
            startX = e.clientX;
            startLeft = el.scrollLeft;
        };
        const onPointerMove = (e: PointerEvent) => {
            if (!pending) return;
            const dx = e.clientX - startX;
            if (!dragging) {
                if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
                // تجاوزنا العتبة: الآن فقط نلتقط المؤشّر ليستمرّ السحب خارج الحاوية.
                dragging = true;
                el.setPointerCapture(e.pointerId);
                el.classList.add('is-dragging');
            }
            e.preventDefault();
            // فرق بصري خام: يعمل في الاتجاهين بلا معالجة RTL — سحب المحتوى يميناً
            // يعني دائماً scrollLeft أصغر أياً كان اتجاه الكتابة.
            el.scrollLeft = startLeft - dx;
        };
        const endDrag = (e: PointerEvent) => {
            if (!pending) return;
            pending = false;
            if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
            if (dragging) {
                dragging = false;
                el.classList.remove('is-dragging');
                // ابتلاع النقرة التالية فقط: سحب انتهى فوق بطاقة يجب ألّا يُقرأ اختياراً لها.
                el.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                }, { capture: true, once: true });
            }
        };

        // passive:false لازم لأن onWheel يستدعي preventDefault.
        el.addEventListener('wheel', onWheel, { passive: false });
        el.addEventListener('pointerdown', onPointerDown);
        el.addEventListener('pointermove', onPointerMove);
        el.addEventListener('pointerup', endDrag);
        el.addEventListener('pointercancel', endDrag);
        return () => {
            el.removeEventListener('wheel', onWheel);
            el.removeEventListener('pointerdown', onPointerDown);
            el.removeEventListener('pointermove', onPointerMove);
            el.removeEventListener('pointerup', endDrag);
            el.removeEventListener('pointercancel', endDrag);
        };
    }, [el]);

    return setEl;
}

interface CycloneTrackerProps {
    /** الزمن الصالح للإطار المعروض حالياً (ملّي ثانية) — لتحريك أيقونة الإعصار على مساره. */
    frameValidEpochMs: number;
}

type CycloneDisplay = {
    systems: boolean;
    disturbances: boolean;
    past: boolean;
    future: boolean;
    pointStyle: 'cyclone' | 'dot';
};

const DEFAULT_CYCLONE_DISPLAY: CycloneDisplay = {
    systems: true, disturbances: true, past: true, future: true, pointStyle: 'cyclone',
};
const CYCLONE_DISPLAY_STORAGE_KEY = 'rasid:cyclone-display';

function readCycloneDisplay(): CycloneDisplay {
    try {
        const saved = JSON.parse(localStorage.getItem(CYCLONE_DISPLAY_STORAGE_KEY) ?? '{}');
        return {
            ...DEFAULT_CYCLONE_DISPLAY,
            ...saved,
            pointStyle: saved.pointStyle === 'dot' ? 'dot' : 'cyclone',
        };
    } catch {
        return DEFAULT_CYCLONE_DISPLAY;
    }
}

function saveCycloneDisplay(value: CycloneDisplay): void {
    localStorage.setItem(CYCLONE_DISPLAY_STORAGE_KEY, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent('rasid:cyclone-display', { detail: value }));
}

// يعاد الرسم عند تغيّر frameValidEpochMs (≈6 مرّات/ساعة) لتحديث موضع الأيقونة على المسار؛
// إعادة الرسم رخيصة (لا يُعاد بناء الـ DOM — ذلك في effect بمُدخلات ثابتة).
export const CycloneTracker = memo(function CycloneTracker({ frameValidEpochMs }: CycloneTrackerProps) {
    const mapRef = useMapRef();
    const { t } = useTranslation();
    const {
        visibleLayers,
        layerAnimationSettings,
        dockOpen,
        setDockOpen,
        currentTimeIndex,
        setTimelinePosition,
    } = useWeatherStore(useShallow((s) => ({
        visibleLayers: s.visibleLayers,
        layerAnimationSettings: s.layerAnimationSettings,
        dockOpen: s.cycloneDockOpen,
        setDockOpen: s.setCycloneDockOpen,
        currentTimeIndex: s.currentTimeIndex,
        setTimelinePosition: s.setTimelinePosition,
    })));
    const overlayRef    = useRef<HTMLDivElement | null>(null);
    const svgRef        = useRef<SVGSVGElement | null>(null);
    const cyclonesRef   = useRef<Cyclone[]>([]);
    const tracksRef     = useRef<Map<string, TrackPt[]>>(new Map());
    const markersRef    = useRef<{ el: HTMLElement; lon: number; lat: number; cyc?: Cyclone }[]>([]);
    /* قطعة لكل ضلع بين نقطتين متتاليتين على المسار، ولكلٍّ مسارَان: مصمت ومتقطّع.
       سببه أن الخطّ صار متدرّج اللون كما في Zoom Earth، وSVG لا يلوّن أجزاء مسارٍ
       واحد بألوان مختلفة. العدد ثابت (عدد النقاط ناقص واحد) فلا يتغيّر الـDOM مع
       تقدّم الشريط الزمني — يتغيّر فقط أيّ نصفٍ من الضلع مصمت وأيّه متقطّع. */
    /* عناصر الاضطرابات: مضلّع منطقة التطوّر + علامة نقطتها. */
    const distRef       = useRef<{ area: SVGPathElement; poly: { lon: number; lat: number }[] }[]>([]);
    const pathsRef      = useRef<{
        cone: SVGPathElement;
        coneEdge: SVGPathElement;
        segs: { solid: SVGPathElement; dashed: SVGPathElement }[];
        lineHistory: SVGPathElement;
        cyc: Cyclone;
    }[]>([]);
    const rebuildRef    = useRef<(() => void) | null>(null);
    /** تحديث موضع المسار عند تغيّر الـTimeline، حتى لو لم تتحرك الكاميرا. */
    const positionRef   = useRef<(() => void) | null>(null);
    const tooltipRef    = useRef<HTMLDivElement | null>(null);
    // العلامات تُبنى بـDOM مباشر داخل effect لا يُعاد تشغيله عند تبديل اللغة، فلو أُغلق
    // t داخله لبقي مترجماً بلغة لحظة البناء. ref يُحدَّث كل رسم يُبقيه طازجاً دائماً.
    const tRef          = useRef(t);
    tRef.current = t;
    // effect بناء الـDOM لا يُعاد تشغيله عند تغيّر المتجر (مُدخلاته [mapRef, reduceMotion])،
    // فمستمعو النقر المسجَّلون داخله يقرأون الدالة من ref لا من إغلاق قديم.
    const setDockOpenRef = useRef(setDockOpen);
    setDockOpenRef.current = setDockOpen;
    const [selected, setSelected] = useState<Cyclone | null>(null);
    /** فهرس نقطة المسار المحدَّدة (‎-1 = لا تحديد صريح ⇒ يُبرَز الصفّ الحالي زمنياً). */
    const [activePoint, setActivePoint] = useState(-1);
    // نسخة React (لا ref فقط) من نفس قائمة الأعاصير — لعرض لوحة "الأنظمة النشطة"
    // القابلة للتصفّح (JSX)، بمعزل عن مسار الأداء الحرج (DOM المباشر في rebuild/position
    // أدناه، الذي يبقى يقرأ من cyclonesRef كي لا يُعاد بناء آلاف العقد عند كل إطار).
    const [cyclones, setCyclones] = useState<Cyclone[]>([]);
    /* الاضطرابات المدارية: مناطق يرصدها NHC لاحتمال تكوّنها نظاماً، لا أعاصير.
       منفصلة عن cyclones عمداً — لا مسار لها ولا فئة ولا رياح، فحشرها في نفس
       المصفوفة كان سيفرض حقولاً فارغة على كل نقطة مسار وجدول. */
    const [disturbances, setDisturbances] = useState<Disturbance[]>([]);
    // خيارات عرض مستقلة، كما في Zoom Earth: لا تغيّر بيانات العاصفة أو زمن النموذج.
    const [display, setDisplay] = useState<CycloneDisplay>(readCycloneDisplay);
    const displayRef = useRef(display);
    displayRef.current = display;
    const disturbancesRef = useRef<Disturbance[]>([]);
    /* تبويبا Figma: التتبّع (الأنظمة النشطة/تفاصيلها) ودليل الرموز. الدليل كان
       <details> مطويّاً داخل القائمة، فكان يُدفَن أسفل أنظمة كثيرة ولا يُرى.
       تبويب مستقلّ يجعله في مستوى واحد مع التتبّع لا تابعاً له. */
    const [tab, setTab] = useState<'tracking' | 'legend'>('tracking');

    /*
     * ── ترتيب صفوف المسار: تنازلياً بالزمن ──────────────────────────────────────
     *
     * `track` يصل مرتّباً تصاعدياً وأوّله نقطة «الآن»، فكان الجدول يعرض الحاضر في
     * الصدر ثم المستقبل نازلاً. ومرجع التصميم (‏Zoom Earth) يعكس ذلك: أبعد توقّع في
     * الأعلى، والزمن ينزل حتى «الآن»، ثم الماضي تحته.
     *
     * السبب ليس ذوقاً: القارئ يمسح الجدول من أعلى، والمعلومة التي يبحث عنها في جدول
     * إعصار هي **إلى أين يتّجه**، لا أين هو الآن (فذاك ظاهر على الخريطة أمامه).
     *
     * نحتفظ بالفهرس الأصلي `i` مع كل نقطة: النقر يحرّك الخريطة والشريط الزمني اعتماداً
     * عليه، و`activePoint` يشير إليه. الترتيب عرضٌ فقط ولا يمسّ مرجعية البيانات.
     *
     * ولا صفوف ماضية اليوم: `history_track` من المصدر يحمل إحداثيات بلا أزمنة، فلا
     * يصلح صفوفاً. لذلك ينتهي الجدول عند «الآن» بدل أن يستمرّ تحته.
     */
    const trackRows = useMemo(() => {
        const rows = (selected?.track ?? []).map((point, i) => ({ point, i }));
        const at = (tm: string) => (tm === 'now' ? Date.now() : Date.parse(tm));
        return rows.sort((a, b) => {
            const ta = at(a.point.time), tb = at(b.point.time);
            // أزمنة غير صالحة تُدفع إلى الأسفل بدل أن تُفسد الترتيب بـNaN.
            if (!Number.isFinite(ta)) return 1;
            if (!Number.isFinite(tb)) return -1;
            return tb - ta;
        });
    }, [selected]);

    /* اختيار نقطة على المسار — مصدر واحد يستدعيه الجدولُ والخريطةُ معاً.
       قبله كان لكلٍّ منهما مسلكه: صفّ الجدول يُحرّك الخريطة والشريط الزمني معاً،
       بينما النقر على نقطة على الخريطة كان يكتفي بتحديدها — فيختلف أثر الفعل
       الواحد باختلاف مكان الضغط، وهو ما لاحظه المستخدم. توحيدهما في دالة واحدة
       يمنع افتراقهما ثانيةً بنيوياً لا بالانضباط. */
    const selectTrackPoint = useCallback((cyc: Cyclone, index: number) => {
        const point = cyc.track[index];
        setActivePoint(index);
        if (!point) return;

        const map = mapRef.current;
        map?.flyTo({
            center: [point.lon, point.lat],
            zoom: Math.max(map.getZoom(), 5),
            duration: 800,
        });

        // ونُحرّك الشريط الزمني إلى لحظة النقطة نفسها، فتتوافق الخريطة تحتها مع
        // التوقيت المعروض — الجدول والخريطة والزمن عرضٌ واحد لا ثلاثة.
        const o = trackHourOffset(point.time);
        if (o === null) return;
        // الخطوة ساعة واحدة (meta.step_hours=1)، والفهرس الحالي يقابل
        // frameValidEpochMs، فالإزاحة بينهما تكفي بلا معرفة أصل المحور.
        const deltaH = (Date.now() + o * 3_600_000 - frameValidEpochMsRef.current) / 3_600_000;
        const next = Math.round(currentTimeIndexRef.current + deltaH);
        setTimelinePositionRef.current(Math.max(0, next), 0);
    }, [mapRef]);

    // المراجع تُبقي الدالة ثابتة الهوية رغم تغيّر الإطار الزمني ستّين مرّة في الثانية —
    // بلاها يُعاد بناء طبقة الخريطة مع كل إطار.
    const frameValidEpochMsRef = useRef(frameValidEpochMs);
    frameValidEpochMsRef.current = frameValidEpochMs;
    const currentTimeIndexRef = useRef(currentTimeIndex);
    currentTimeIndexRef.current = currentTimeIndex;
    const setTimelinePositionRef = useRef(setTimelinePosition);
    setTimelinePositionRef.current = setTimelinePosition;

    const selectTrackPointRef = useRef(selectTrackPoint);
    selectTrackPointRef.current = selectTrackPoint;
    // إزاحة وقت الإطار المعروض عن الآن (ساعات) — يقرأها موزّع المواضع (يعمل كل إطار عبر
    // حدث 'render') من ref كي يبقى محدَّثاً دون إعادة تسجيل المستمع.
    const hoursFromNowRef = useRef(0);
    /** سلسلتا المخروط الرسمي لكل إعصار، مع مفتاح الكاميرا الذي بُنيتا عليه. */
    const coneCacheRef = useRef(new Map<string, { key: string; src: unknown; fillD: string; edgeD: string }>());
    hoursFromNowRef.current = Number.isFinite(frameValidEpochMs) ? (frameValidEpochMs - Date.now()) / 3_600_000 : 0;
    useEffect(() => {
        // كان موضع العلامة يتحدّث فقط مع تحريك/تكبير الخريطة. هذا التحديث الخفيف
        // يربطه بإطار التنبؤ نفسه كما في Zoom Earth وWindy.
        positionRef.current?.();
        mapRef.current?.triggerRepaint();
    }, [frameValidEpochMs, mapRef]);
    // الزر يبدّل visibleLayers.hurricanes فقط — كان `|| showHurricanes` (افتراضيه true)
    // يجعل الأعاصير ظاهرة دائماً فيبدو الزر معطّلاً.
    const isVisible   = visibleLayers.hurricanes;
    const reduceMotion = layerAnimationSettings.hurricanes.reduceMotion;
    const stripRef = useHorizontalDragScroll();

    useEffect(() => {
        // طبقة SVG/DOM تُبنى مباشرةً للأداء، لذا نعيد بناءها فقط عند تغيير خيار العرض.
        rebuildRef.current?.();
    }, [display]);

    useEffect(() => {
        saveCycloneDisplay(display);
    }, [display]);

    useEffect(() => {
        const sync = (event: Event) => setDisplay((event as CustomEvent<CycloneDisplay>).detail);
        window.addEventListener('rasid:cyclone-display', sync);
        return () => window.removeEventListener('rasid:cyclone-display', sync);
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const container = map.getContainer();
        const overlay   = document.createElement('div');
        overlay.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:10;overflow:hidden;';
        container.appendChild(overlay);
        overlayRef.current = overlay;

        // طبقة SVG واحدة تحت العلامات: خطّ المسار (متصل ثم متقطّع) ومخروط عدم اليقين.
        // pointer-events:none — لا تعترض النقر، النقاط/الأيقونة الفعلية (DOM) هي القابلة للنقر.
        const svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('class', 'cyc-track-svg');
        svg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible;';
        overlay.appendChild(svg);
        svgRef.current = svg;

        // تعليق النقطة — عنصر واحد مشترك يُعاد استعماله لكل النقاط (لا عنصر لكل نقطة:
        // مسارات عدّة أعاصير تعني عشرات العقد الدائمة بلا داعٍ).
        const tooltip = document.createElement('div');
        tooltip.className = 'cyc-tip';
        // يُلحق بحاوية الخريطة لا بطبقة العلامات: الطبقة نفسها z-index:10 داخل الحاوية،
        // فأي رقم داخلها يبقى تحت .hover-value-tooltip (1300) ويُحجب سطر الرياح. خارجها
        // يصير نظيراً لها في نفس سياق التكديس فيعلوها. الحاوية inset:0 مطابقة للطبقة،
        // فحساب الموضع النسبي لا يتغيّر.
        container.appendChild(tooltip);
        tooltipRef.current = tooltip;

        // سلوك اللمس يختلف عن hover: اللمس يكشف المعلومة للحظة، لكنه لا يثبّت
        // بطاقة فوق الخريطة. أي محاولة لتحريك/تكبير الخريطة أو لمس موضع آخر تُخفيها.
        // هذا يمنع البطاقة من السفر مع الخريطة على الهاتف.
        const dismissTooltip = () => tooltip.classList.remove('is-visible');
        const dismissOnPointerDown = () => dismissTooltip();
        container.addEventListener('pointerdown', dismissOnPointerDown, true);
        map.on('dragstart', dismissTooltip);
        map.on('movestart', dismissTooltip);
        map.on('zoomstart', dismissTooltip);
        map.on('rotatestart', dismissTooltip);
        map.on('pitchstart', dismissTooltip);

        // إعادة تموضع فقط عند كل إطار — لا نعيد بناء الـ DOM كي لا تُعاد الأنيميشن
        // من الصفر باستمرار (كانت العلامات "تفتح وتغلق" لأن innerHTML كان يُستبدل
        // مع كل حدث render).
        let lastPositionKey: string | null = null;
        const position = (force = false) => {
            const mapObj = mapRef.current;
            if (!mapObj) return;
            // خريطة ساكنة ⇒ لا موضع يتغيّر. `render` يُطلق ستّين مرّة في الثانية بسبب طلب
            // إعادة الرسم من طبقة الجسيمات، لا لأن الخريطة تحرّكت. راجع mapCameraKey.
            const hoursFromNow = hoursFromNowRef.current;
            // زمن الإطار جزء من المفتاح؛ كانت الكاميرا وحدها تمنع تحديث العلامة
            // عندما يسير الشريط الزمني والخريطة ساكنة.
            /*
             * ── تكميم الزمن ببكسلٍ لا بدقيقة ──────────────────────────────────
             *
             * زمن الإطار جزءٌ من المفتاح لأن العلامة تسير على المسار والمخروط يضيق
             * أمامها. لكن الدقيقة كانت وحدةً أدقّ ممّا تُظهره الشاشة: الإعصار يزحف
             * نحو عشرين كيلومتراً في الساعة، أي ثلاثمئة متر في الدقيقة — دون البكسل
             * الواحد عند التكبيرات الواسعة.
             *
             * فكان الحارس يسقط عند كل دقيقة يتقدّمها الشريط، ويُعاد إسقاط كل مسار
             * وتُبنى سلاسل `path` لكل مخروط من جديد، لتخرج الصورة نفسها بالضبط.
             * وتتبّع 2026-09-20 وضعه ثاني أثقل بند تطبيقي على الخيط الرئيسي (~2.5
             * ثانية) أثناء تشغيل الرادار — وهو ليس من الرادار في شيء، لكنه يستيقظ
             * مع كل خطوة مزجٍ فيه.
             *
             * والتكميم يتبع التكبير لا رقماً ثابتاً: عند 7 فأعلى تبقى الدقيقة لأن
             * البكسل يساوي مئات الأمتار هناك، وتحتها خمس دقائق — أي خُمس العمل،
             * وبفارقٍ يقلّ عن بكسل واحد.
             */
            const quantum = mapObj.getZoom() >= 7 ? 1 : 5;
            const key = `${cameraKey(mapObj)}:${Math.round(hoursFromNow * 60 / quantum)}`;
            if (!force && key === lastPositionKey) return;
            lastPositionKey = key;
            // النقاط تُقاس بالبكسل فتبدو عملاقة عند التصغير الواسع: مسار كامل يتقلّص إلى
            // بضع مئات من البكسلات بينما تبقى كل نقطة 20px، فتتلاصق وتبتلع المسار نفسه.
            // نُصغّرها مع الزووم كما يفعل Zoom Earth (نقاط صغيرة عند رؤية المحيط كلّه،
            // وتكبر كلما اقتربت). المدى 0.55..1 عند التكبير 2..6.
            const z = mapObj.getZoom();
            /*
             * ── لَفّ خطّ الطول إلى النسخة المعروضة ─────────────────────────────────
             * `project` يضع أي إحداثيّ في نسخة العالم **الأساسية** دائماً. ومع
             * renderWorldCopies قد يكون المستخدم ناظراً إلى النسخة المجاورة (شرق خطّ
             * التاريخ مثلاً)، فتُرسم علامة الإعصار في نسخة ومسارُه — المفكوك حول موضعه
             * الحالي — في أخرى: يبدو الإعصار مقطّعاً، رمزه هنا وذيله هناك، أو يختفي
             * نصفه عند الحدّ. فنفكّ كل خطوط الطول حول مركز العرض: كل هندسة الإعصار
             * تسقط في النسخة التي ينظر إليها المستخدم فعلاً.
             */
            const viewLon = mapObj.getCenter().lng;
            const scale = Math.max(0.55, Math.min(1, 0.55 + (z - 2) * 0.1125));
            overlay.style.setProperty('--cyc-dot-scale', scale.toFixed(3));
            /* رمز العاصفة نفسه يتبع المبدأ ذاته بمدى أضيق: هو العنصر الذي يجب أن
               يُرى ويُضغط، فلا ينزل إلى حجم نقطة المسار. 0.7 عند رؤية العالم كلّه
               وصولاً إلى الحجم الكامل عند التكبير 4 — يكفي لإزالة الضخامة عند
               التصغير الواسع مع بقاء الرمز هدفاً واضحاً للمس. */
            const markerScale = Math.max(0.7, Math.min(1, 0.7 + (z - 1) * 0.1));
            overlay.style.setProperty('--cyc-marker-scale', markerScale.toFixed(3));
            for (const m of markersRef.current) {
                // الأيقونة الرئيسية (m.cyc موجود) تُستوفى على المسار حسب وقت الإطار الحالي
                // فتلاحق الإعصار؛ نقاط المسار تبقى ثابتة عند مواضعها المتوقّعة.
                let lon = m.lon, lat = m.lat;
                if (m.cyc) {
                    const pts = tracksRef.current.get(m.cyc.id) ?? [];
                    const p = cyclonePositionAt(pts, m.cyc, hoursFromNow);
                    lon = p.lon; lat = p.lat;
                    // البؤرة ليست دبوساً ثابتاً: تتبنّى الفئة المتوقعة عند زمن
                    // الإطار، فيتحرك الرمز ولونه مع حقل النموذج والمسار معاً.
                    const color = categoryColor(p.cat);
                    if (m.el.dataset.cycloneLevel !== String(p.cat)) {
                        m.el.dataset.cycloneLevel = String(p.cat);
                        m.el.style.setProperty('--cyc-color', color);
                        const image = m.el.querySelector<HTMLImageElement>('.cyc-eye-img');
                        if (image) image.src = categoryIcon(p.cat);
                        const badge = m.el.querySelector<HTMLElement>('.cyc-cat-badge');
                        if (badge) {
                            badge.style.background = color;
                            badge.textContent = levelBadge(p.cat);
                        }
                    }
                }
                const pt = mapObj.project([unwrapLon(lon, viewLon), lat]);
                m.el.style.left = `${pt.x}px`;
                m.el.style.top  = `${pt.y}px`;
            }

            // خطّ المسار + المخروط: يُعادان حسابهما كل إطار (رخيص، بضع نقاط لكل إعصار) كي
            // يتحرّك حدّ "المصمت/المتقطّع" مع تقدّم الشريط الزمني — يترك الإعصار خلفه أثراً
            // مصمتاً لما قطعه فعلياً، والباقي توقّع متقطّع داخل مخروطه.
            for (const { cone, coneEdge, segs, lineHistory, cyc } of pathsRef.current) {
                const options = displayRef.current;
                const pts = tracksRef.current.get(cyc.id) ?? [];
                const cur = cyclonePositionAt(pts, cyc, hoursFromNow);
                // مرجع الفكّ هو موضع الإعصار **بعد** فكّه إلى النسخة المعروضة، وإلا
                // بقي المسار كلّه في النسخة الأساسية بينما العلامة في المعروضة.
                const curLon = unwrapLon(cur.lon, viewLon);
                const at = (lon: number, lat: number) => mapObj.project([unwrapLon(lon, curLon), lat]);
                // المسار المنقضي مستقلّ عن نقاط التوقّع: يُرسم حتى لو خلت العاصفة من مسار
                // متوقّع (عاصفة تنتهي مثلاً)، فيبقى "من أين أتت" ظاهراً دائماً.
                lineHistory.setAttribute('d', options.past && cyc.history.length > 1
                    ? `M ${cyc.history.map((p) => {
                        const s = at(p.lon, p.lat);
                        return `${s.x.toFixed(1)} ${s.y.toFixed(1)}`;
                    }).join(' L ')}`
                    : '');

                if (pts.length < 2 || !options.systems) {
                    cone.setAttribute('d', '');
                    coneEdge.setAttribute('d', '');
                    for (const seg of segs) { seg.solid.setAttribute('d', ''); seg.dashed.setAttribute('d', ''); }
                    continue;
                }
                const curScreen = mapObj.project([curLon, cur.lat]);

                // النقاط المتبقّية أمام الموضع الحالي فقط — المقطوعة تُهمَل من الرسم.
                const ahead = pts.filter((p) => p.o > hoursFromNow);

                /* لكل ضلع: هل هو خلف اللحظة الحالية كلّه (مصمت)، أمامها كلّه (متقطّع)،
                   أم يعبرها؟ الضلع العابر يُقسَم عند الموضع الحالي بالضبط — فيلتقي طرفا
                   المصمت والمتقطّع عند نقطة واحدة بلا فجوة، وهو ما كان يضمنه الخطّان
                   المفردان قبل التجزئة. */
                const seg = (a: { x: number; y: number }, b: { x: number; y: number }) =>
                    `M ${a.x.toFixed(1)} ${a.y.toFixed(1)} L ${b.x.toFixed(1)} ${b.y.toFixed(1)}`;

                for (let i = 0; i < segs.length; i++) {
                    const p0 = pts[i], p1 = pts[i + 1];
                    if (!p0 || !p1) { segs[i].solid.setAttribute('d', ''); segs[i].dashed.setAttribute('d', ''); continue; }
                    const partVisible = p1.o <= 0 ? options.past : options.future;
                    if (!partVisible) { segs[i].solid.setAttribute('d', ''); segs[i].dashed.setAttribute('d', ''); continue; }
                    if (!isPlausibleTrackSegment(p0, p1)) {
                        segs[i].solid.setAttribute('d', ''); segs[i].dashed.setAttribute('d', ''); continue;
                    }
                    const s0 = at(p0.lon, p0.lat);
                    const s1 = at(p1.lon, p1.lat);

                    if (p1.o <= hoursFromNow) {
                        segs[i].solid.setAttribute('d', seg(s0, s1));
                        segs[i].dashed.setAttribute('d', '');
                    } else if (p0.o >= hoursFromNow) {
                        segs[i].solid.setAttribute('d', '');
                        segs[i].dashed.setAttribute('d', seg(s0, s1));
                    } else {
                        segs[i].solid.setAttribute('d', seg(s0, curScreen));
                        segs[i].dashed.setAttribute('d', seg(curScreen, s1));
                    }
                }

                // هالة عدم يقين ناعمة — لا مخروط NHC الحرفي (كان يظهر كمثلث صلب حادّ
                // الحواف يبتلع الشاشة، وهو ما رُفض بصرياً). نفس الحساب الجغرافي بالكيلومترات
                // (يتناسب مع الزووم) لكن نصف قطر أصغر بكثير + منحنى ناعم (Q تربيعي بمنتصف
                // كل ضلع، لا خطوط مستقيمة حادّة) + شفافية أخفّ + ضبابية CSS — يقرأ كـ"توهّج
                // جوّي" خلف الخطّ لا كشكل هندسي منفصل.
                // المخروط الرسمي من NHC متى توفّر: مضلّع مبني على أخطاء التوقّع التاريخية
                // لكل حوض، وهو ما تعرضه Zoom Earth/NHC — أدقّ من أي تقدير محلّي. نرسمه
                // كما هو ونتخطّى الحساب التقديري بالكامل.
                /* المخروط الرسمي: حلقة أو أكثر، كلٌّ مسارٌ فرعيّ مستقلّ في نفس الـpath.
                   `fill-rule: nonzero` يوحّدها فتُقرأ شكلاً واحداً.

                   فكّ التفاف خطّ الطول ضروري هنا: المصدر يقسم المخروط عند خطّ التاريخ
                   الدولي إلى مضلّعٍ ينتهي عند ‎180 وآخر يبدأ من ‎-180. لو أُسقطت
                   الإحداثيات كما هي لانفصل نصفا الشكل إلى طرفَي الخريطة وبينهما شريط
                   يعبر العالم. نُرجع كل نقطة إلى أقرب نسخة لها من مركز العاصفة (±360)
                   قبل الإسقاط، فيلتحم النصفان مكانهما الصحيح. */
                cone.classList.remove('is-estimated');
                if (options.future && cyc.cone.length > 0) {
                    /*
                     * ── المخروط الرسمي لا يتعلّق بالزمن ─────────────────────────────
                     *
                     * حلقاته ثابتةٌ من المصدر، ولا يدخل `hoursFromNow` في بنائها — الكاميرا
                     * وحدها ونسخة العالم التي تسكنها العاصفة. ومع ذلك كانت تُعاد إسقاط كل
                     * نقاطها وتُبنى سلسلتها النصّية مع كل خطوة زمنية، أي مع كل خطوة
                     * رادار. وتتبّع 2026-09-21 وضع بناء هذه السلاسل في رأس ما بقي على
                     * الخيط الرئيسي بعد البرق (~1.9 ثانية). فتُخزَّن لكل إعصار وتُعاد
                     * حين تتحرّك الكاميرا أو تنتقل العاصفة إلى نسخة عالمٍ أخرى فقط.
                     */
                    const ckey = `${cameraKey(mapObj)}|${Math.round(curLon / 360)}`;
                    const hit = coneCacheRef.current.get(cyc.id);
                    // هويّة المصفوفة لا عددها: نشرةٌ جديدة قد تأتي بنفس عدد الحلقات.
                    if (hit && hit.key === ckey && hit.src === cyc.cone) {
                        if (hit.fillD) {
                            if (cone.getAttribute('d') !== hit.fillD) cone.setAttribute('d', hit.fillD);
                            if (coneEdge.getAttribute('d') !== hit.edgeD) coneEdge.setAttribute('d', hit.edgeD);
                            continue;
                        }
                    }
                    // نقطة على خطّ التاريخ بالضبط = طرف درزة الانقسام، لا حافّة حقيقية.
                    const onSeam = (lon: number) => Math.abs(Math.abs(lon) - 180) < 0.01;

                    const rings = cyc.cone.filter((ring) => ring.length >= 4);
                    const fillD = rings.map((ring) => {
                        const pts = ring.map((pt) => at(pt.lon, pt.lat));
                        return `M ${pts.map((pt) => `${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`).join(' L ')} Z`;
                    }).join(' ');

                    /* الحدّ: نمشي على الحلقة ونقطع الخطّ عند كل ضلع طرفاه على الدرزة —
                       فيُرسم المحيط الخارجي وحده وتبقى الدرزة غير مرئية. */
                    const edgeD = rings.map((ring) => {
                        const closed = [...ring, ring[0]];
                        let out = '';
                        let pen = false;
                        for (let i = 0; i + 1 < closed.length; i++) {
                            const a = closed[i], b = closed[i + 1];
                            if (onSeam(a.lon) && onSeam(b.lon)) { pen = false; continue; }
                            const pa = at(a.lon, a.lat);
                            const pb = at(b.lon, b.lat);
                            if (!pen) { out += ` M ${pa.x.toFixed(1)} ${pa.y.toFixed(1)}`; pen = true; }
                            out += ` L ${pb.x.toFixed(1)} ${pb.y.toFixed(1)}`;
                        }
                        return out.trim();
                    }).filter(Boolean).join(' ');

                    coneCacheRef.current.set(cyc.id, { key: ckey, src: cyc.cone, fillD, edgeD });
                    if (fillD) {
                        cone.setAttribute('d', fillD);
                        coneEdge.setAttribute('d', edgeD);
                        continue;
                    }
                }
                coneEdge.setAttribute('d', '');
                if (!options.future) { cone.setAttribute('d', ''); continue; }

                // لا مخروط رسمي ⇒ تقدير محلّي. يُوسَم كي لا يُرسم له حدّ: التقدير سلسلة
                // دوائر متداخلة، وحدُّها يُظهر حافّة كل دائرة على حدة فيبدو كعقد خرز لا
                // كمخروط. التعبئة وحدها تتّحد بـfill-rule فتُقرأ شكلاً ناعماً واحداً.
                cone.classList.add('is-estimated');
                // مخروط عدم اليقين = اتّحاد دوائر متنامية على طول المسار المتوقّع، لا شريط
                // بإزاحة عمودية. الطريقة القديمة كانت تحسب عمودياً على اتجاه كل نقطة ثم تصل
                // الحافّتين بمنحنيات: عند أي انعطاف حادّ في المسار تتقاطع الحافّتان مع نفسيهما
                // فينفتح الشكل كمثلث عملاق منحرف عن المسار (وهو الخلل الظاهر على الخريطة).
                // دوائر متتالية بنفس اتجاه الرسم تتّحد تلقائياً بـ fill-rule:nonzero، فتُنتج
                // دائماً مخروطاً ناعماً يتبع المسار مهما انحنى — ولا يمكنه أن ينفتح أصلاً.
                const geoChain = [{ lon: cur.lon, lat: cur.lat, o: hoursFromNow }, ...ahead];
                if (geoChain.length < 2) { cone.setAttribute('d', ''); continue; }
                const KM_PER_DEG_LAT = 111.32;
                let estimatedD = '';
                for (const g of geoChain) {
                    // نصف قطر تلميحي خفيف (12كم + 1.3كم/ساعة) ينمو مع بُعد التوقّع زمنياً.
                    const rKm = Math.max(0, g.o - hoursFromNow) * 1.3 + 12;
                    const cosLat = Math.cos(g.lat * Math.PI / 180) || 0.01;
                    // نصف القطر يُقاس على الشاشة من فرق إسقاط نقطتين تفصلهما rKm فعلياً —
                    // فيظلّ صحيحاً مع الزووم ومع تشوّه مركاتور عند خطوط العرض العالية.
                    const c0 = at(g.lon, g.lat);
                    const c1 = at(g.lon + (rKm / (KM_PER_DEG_LAT * cosLat)), g.lat);
                    const rPx = Math.abs(c1.x - c0.x);
                    if (!(rPx > 0.5)) continue;
                    const x = c0.x.toFixed(1), y0 = (c0.y - rPx).toFixed(1), y1 = (c0.y + rPx).toFixed(1);
                    const r = rPx.toFixed(1);
                    estimatedD += ` M ${x} ${y0} A ${r} ${r} 0 1 1 ${x} ${y1} A ${r} ${r} 0 1 1 ${x} ${y0} Z`;
                }
                cone.setAttribute('d', estimatedD.trim());
            }

            // مضلّعات مناطق التطوّر المحتمل — تُسقَط إلى الشاشة كأي هندسة أخرى.
            for (const { area, poly } of distRef.current) {
                if (poly.length < 3) { area.setAttribute('d', ''); continue; }
                // المضلّع يُفكّ حول رأسه الأول (المفكوك بدوره إلى النسخة المعروضة): بلا
                // ذلك يُمَدّ ضلعٌ واحد عبر الخريطة كلّها متى عبر المضلّع خطّ التاريخ.
                const ringLon0 = unwrapLon(poly[0].lon, viewLon);
                const ring = poly.map((pt) => mapObj.project([unwrapLon(pt.lon, ringLon0), pt.lat]));
                area.setAttribute('d', `M ${ring.map((pt) => `${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`).join(' L ')} Z`);
            }
        };

        // يُبنى الـ DOM مرة واحدة عند تغيّر البيانات فقط.
        const rebuild = () => {
            const host = overlayRef.current;
            const svgHost = svgRef.current;
            if (!host || !svgHost) return;
            // نمسح كل شيء عدا طبقة الـ SVG وعنصر التعليق (كلاهما يُنشأ مرّة واحدة في
            // الـeffect ولا يُعاد بناؤه). حذف التعليق هنا كان يترك tooltipRef يشير إلى
            // عقدة منفصلة عن الصفحة، فلا يظهر شيء عند المرور مهما عمل المستمع.
            const keep = new Set<Element>([svgHost, tooltipRef.current as Element].filter(Boolean) as Element[]);
            for (const child of Array.from(host.children)) if (!keep.has(child)) child.remove();
            svgHost.innerHTML = '';
            markersRef.current = [];
            distRef.current = [];
            pathsRef.current = [];
            tracksRef.current = new Map(cyclonesRef.current.map((c) => [c.id, orderedTrack(c)]));

            // النقر على علامة فوق الخريطة يفتح المرسى أيضاً — وفتحه يمرّ عبر المتجر فيُغلق
            // بقية اللوحات على الجوّال تلقائياً، تماماً كفتحه من لسانه.
            // pointIdx: فهرس نقطة المسار التي نُقر عليها (‎-1 لأيقونة العاصفة نفسها).
            // تمريره يجعل صفّ الجدول المقابل يُحدَّد فوراً — الخريطة واللوحة عرضان لشيء
            // واحد، فالنقر على نقطة هناك يجب أن يُبرز سطرها هنا (سلوك Zoom Earth).
            const onClick = (id: string, pointIdx = -1) => (e: Event) => {
                e.stopPropagation();
                const hit = cyclonesRef.current.find(c => c.id === id) ?? null;
                setSelected(hit);
                if (hit) setDockOpenRef.current(true);
                // نقطة مسار بعينها ⇒ نفس أثر الضغط على سطرها في الجدول بالضبط
                // (تحديد + طيران + نقل الشريط الزمني). الأيقونة الرئيسية (‎-1) تكتفي
                // بفتح اللوحة: لا نقطة زمنية بعينها قُصدت.
                if (hit && pointIdx >= 0) selectTrackPointRef.current(hit, pointIdx);
                else setActivePoint(pointIdx);
            };

            /**
             * تعليق النقطة بأسلوب Zoom Earth: الاسم، اليوم والساعة، الرياح، ثم اسم الفئة —
             * مصبوغ بلون الفئة نفسها فيُقرأ مستواها قبل قراءة نصّه. يظهر بالمرور على سطح
             * المكتب وبالضغط على اللمس (حيث لا وجود لـhover أصلاً).
             */
            const attachTip = (
                el: HTMLElement,
                cyc: Cyclone,
                point: Cyclone['track'][number],
                color: string,
            ) => {
                const show = () => {
                    const tip = tooltipRef.current;
                    const hostEl = overlayRef.current;
                    // `point` قد يصل undefined إن جاء مصدرٌ بعاصفة بلا نقاط مسار — أسقط
                    // ذلك المتتبّع كلّه سابقاً (موجز GDACS بلا best track). المصدر أُصلح
                    // ليضمن نقطة «الآن»، ويبقى هذا الحارس كي لا يُسقط مصدرٌ جديد الواجهة.
                    if (!tip || !hostEl || !point) return;
                    const tt = tRef.current;

                    const when = point.time === 'now' ? new Date() : new Date(point.time);
                    const whenText = Number.isFinite(when.getTime())
                        ? formatTipWhen(when, tt)
                        : tt('cyclone.now');
                    // اسم الدرجة من السلّم الموحّد لا من ثنائية «فئة N / عاصفة مدارية»:
                    // تلك كانت تسمّي المنخفض المداري عاصفةً، وهو تصنيف آخر.
                    const catText = tt(levelNameKey(point.cat));

                    tip.style.setProperty('--tip-color', color);
                    // سطر لكل قيمة بـtextContent لا innerHTML: أسماء العواصف تأتي من موجز
                    // خارجي، فلا تُمرَّر أبداً كـHTML.
                    tip.replaceChildren();
                    for (const [text, cls] of [
                        [cyc.nameAr, 'cyc-tip-name'],
                        [whenText, 'cyc-tip-when'],
                        [`${Math.round(point.wind)} km/h`, 'cyc-tip-wind'],
                        [catText, 'cyc-tip-cat'],
                    ] as const) {
                        const line = document.createElement('div');
                        line.className = cls;
                        line.textContent = text;
                        tip.appendChild(line);
                    }

                    const r = el.getBoundingClientRect();
                    const hr = hostEl.getBoundingClientRect();
                    tip.style.left = `${r.left - hr.left + r.width / 2}px`;
                    tip.style.top = `${r.top - hr.top}px`;
                    tip.classList.add('is-visible');
                };
                const hide = () => tooltipRef.current?.classList.remove('is-visible');

                el.addEventListener('mouseenter', show);
                el.addEventListener('mouseleave', hide);
                // ننتظر انتهاء اللمسة: إن تحولت إلى سحب ستستقبل الخريطة dragstart
                // ولا تُفتح بطاقة عالقة؛ أما النقرة الصريحة فتُظهرها.
                el.addEventListener('pointerup', (event) => {
                    if (event.pointerType === 'touch') show();
                });
            };

            /**
             * تلميح منطقة التطوّر المحتمل — بنية Zoom Earth نفسها (اسم، صفة، ثم القيم).
             *
             * ما لا يُعرض عمداً: سرعة الحركة واتجاهها. Zoom Earth يعرضهما لأنه يقرأ نشرة
             * الـinvest التشغيلية، أمّا مصدرنا (نشرة التوقّعات المدارية GTWO) فلا يحمل
             * متّجه حركة أصلاً — فكتابة رقمٍ هنا اختلاقٌ لا نقل. نعرض بدلاً منه ما يحمله
             * المصدر فعلاً وهو جوهر المنتج: احتمالا التكوّن خلال يومين وسبعة أيام.
             */
            const attachDistTip = (el: HTMLElement, dist: Disturbance) => {
                const show = () => {
                    const tip = tooltipRef.current;
                    const hostEl = overlayRef.current;
                    if (!tip || !hostEl) return;
                    const tt = tRef.current;

                    tip.style.setProperty('--tip-color', DISTURBANCE_COLOR);
                    tip.replaceChildren();
                    for (const [text, cls] of [
                        [`${dist.basin} ${dist.area}`.trim(), 'cyc-tip-name'],
                        [tt('cyclone.developmentArea'), 'cyc-tip-when'],
                        [tt('cyclone.prob2day', { n: dist.prob2day }), 'cyc-tip-wind'],
                        [tt('cyclone.prob7day', { n: dist.prob7day }), 'cyc-tip-cat'],
                    ] as const) {
                        const line = document.createElement('div');
                        line.className = cls;
                        line.textContent = text;
                        tip.appendChild(line);
                    }

                    const r = el.getBoundingClientRect();
                    const hr = hostEl.getBoundingClientRect();
                    tip.style.left = `${r.left - hr.left + r.width / 2}px`;
                    tip.style.top = `${r.top - hr.top}px`;
                    tip.classList.add('is-visible');
                };
                const hide = () => tooltipRef.current?.classList.remove('is-visible');

                el.addEventListener('mouseenter', show);
                el.addEventListener('mouseleave', hide);
                el.addEventListener('pointerup', (event) => {
                    if (event.pointerType === 'touch') show();
                });
            };

            /* الاضطرابات تُبنى أوّلاً فتقع مضلّعاتها **تحت** مسارات الأعاصير:
               مساحة التطوّر رقعة عريضة، ولو رُسمت فوق مسارٍ لحجبته. */
            for (const d of disturbancesRef.current) {
                if (!displayRef.current.disturbances) continue;
                const area = document.createElementNS(SVG_NS, 'path');
                area.setAttribute('class', 'cyc-dist-area');
                /* شفافية التعبئة تتبع احتمال التكوّن خلال 7 أيام: منطقة باحتمال 80%
                   تُقرأ أوضح من أخرى باحتمال 10%، فتتدرّج الأهمّية بصرياً بلا أسطورة
                   إضافية. الحدّان 0.10..0.30 جُرّبا كي تبقى الخريطة مقروءة تحتها. */
                const weight = Math.max(0, Math.min(100, d.prob7day)) / 100;
                area.style.fillOpacity = (0.10 + weight * 0.20).toFixed(3);
                svgHost.appendChild(area);
                distRef.current.push({ area, poly: d.area_polygon });

                const el = document.createElement('div');
                el.className = 'cyc-dist-marker';
                el.style.cssText = 'position:absolute;transform:translate(-50%,-50%);pointer-events:auto;';
                el.innerHTML = `<img src="${levelIcon('disturbance')}" alt="" aria-hidden="true" draggable="false" />`;
                attachDistTip(el, d);
                // المضلّع نفسه يستقبل المرور أيضاً: مساحته أكبر من النقطة بمراتب،
                // وهو ما يُشير إليه المستخدم فعلاً حين يقصد «منطقة التطوّر».
                area.style.pointerEvents = 'auto';
                attachDistTip(area as unknown as HTMLElement, d);
                host.appendChild(el);
                markersRef.current.push({ el, lon: d.lon, lat: d.lat });
            }

            for (const c of cyclonesRef.current) {
                if (!displayRef.current.systems) continue;
                const color = categoryColor(c.category);

                // مخروط + مسار منقضٍ + قطع التوقّع — كلّها عناصر SVG تحت العلامات.
                const cone = document.createElementNS(SVG_NS, 'path');
                cone.setAttribute('class', 'cyc-cone');
                cone.style.fill = color;
                svgHost.appendChild(cone);

                /* حدّ المخروط في عنصر مستقلّ عن تعبئته.
                   السبب: حين ينقسم المخروط عند خطّ التاريخ إلى حلقتين، تشترك الحلقتان
                   في حافّة وهمية على الخطّ نفسه. لو حمل عنصرُ التعبئة حدّاً لرُسمت تلك
                   الحافّة خطّاً يشقّ المخروط نصفين — وهو ما ظهر فعلاً. الفصل يسمح برسم
                   التعبئة موحّدةً (fill-rule) ورسم الحدّ على المحيط الحقيقي وحده. */
                const coneEdge = document.createElementNS(SVG_NS, 'path');
                coneEdge.setAttribute('class', 'cyc-cone-edge');
                svgHost.appendChild(coneEdge);
                // المسار المنقضي (best track): يُرسم أوّلاً فيقع تحت قطع التوقّع، وبلون
                // العاصفة الحالي لكن أرفع وأخفت — أثر مضى لا توقّع قادم. يبقى خطّاً
                // واحداً لا قطعاً: نقاط history تحمل إحداثيات بلا فئة، فلا أساس لتلوينها.
                const lineHistory = document.createElementNS(SVG_NS, 'path');
                lineHistory.setAttribute('class', 'cyc-line cyc-line-history');
                lineHistory.style.stroke = color;
                lineHistory.style.color = color;
                svgHost.appendChild(lineHistory);

                /* قطع المسار: ضلع لكل زوج نقاط متتاليتين، ملوّن بفئة نقطته الأولى.
                   هكذا يقرأ المستخدم تصاعد الشدّة أو انحسارها من الخطّ نفسه بلا نقر —
                   وهو ما يفعله Zoom Earth: أخضر حيث كانت عاصفة مدارية، أصفر ثم برتقالي
                   حيث اشتدّت. الخطّ الواحد بلون واحد كان يُخفي هذه المعلومة كلّها. */
                const orderedPts = orderedTrack(c);
                const segs: { solid: SVGPathElement; dashed: SVGPathElement }[] = [];
                for (let i = 0; i + 1 < orderedPts.length; i++) {
                    const segColor = categoryColor(orderedPts[i].cat);
                    const solid = document.createElementNS(SVG_NS, 'path');
                    solid.setAttribute('class', 'cyc-line cyc-line-solid');
                    solid.style.stroke = segColor;
                    solid.style.color = segColor;
                    svgHost.appendChild(solid);

                    const dashed = document.createElementNS(SVG_NS, 'path');
                    dashed.setAttribute('class', 'cyc-line cyc-line-dashed');
                    dashed.style.stroke = segColor;
                    dashed.style.color = segColor;
                    svgHost.appendChild(dashed);

                    segs.push({ solid, dashed });
                }

                pathsRef.current.push({ cone, coneEdge, segs, lineHistory, cyc: c });


                {
                    const el = document.createElement('div');
                    el.className = 'cyclone-marker';
                    el.dataset.cycloneLevel = String(c.category);
                    el.style.cssText = `position:absolute;transform:translate(-50%,-50%);--cyc-color:${color};pointer-events:auto;`;
                // سرعة النبض تعكس الخطورة: فئة 5 تنبض أسرع بضعف سرعة عاصفة مدارية هادئة.
                const pulseDur = Math.max(1.4, 3.2 - c.category * 0.32);
                    el.innerHTML = `
                    <div class="cyc-marker-core">
                        <span class="cyc-pulse${reduceMotion ? ' reduced-motion' : ''}" style="animation-duration:${pulseDur}s"></span>
                        <span class="cyc-pulse cyc-pulse-2${reduceMotion ? ' reduced-motion' : ''}" style="animation-duration:${pulseDur}s;animation-delay:${pulseDur / 2}s"></span>
                        <div class="cyc-eye${reduceMotion ? ' reduced-motion' : ''}"><img class="cyc-eye-img" src="${categoryIcon(c.category)}" alt="" aria-hidden="true" draggable="false" /></div>
                    </div>
                    <div class="cyc-info"><span class="cyc-name">${c.nameAr}</span><span class="cyc-cat-badge" style="background:${color}">${levelBadge(c.category)}</span></div>`;
                    el.addEventListener('click', onClick(c.id));
                // العاصفة نفسها تحمل تعليقها أيضاً (نقطة "الآن" في مسارها).
                    attachTip(el, c, c.track[0], color);
                    host.appendChild(el);
                // cyc مرفق بالأيقونة الرئيسية فقط → position() يستوفي موضعها على المسار.
                    markersRef.current.push({ el, lon: c.lon, lat: c.lat, cyc: c });
                }

                for (let i = 1; i < c.track.length; i++) {
                    // نقطة مصمتة بلا رقم — أسلوب Zoom Earth. الرقم داخل كل نقطة كان يضيف
                    // ضجيجاً بصرياً على مسار طويل بينما اللون وحده يكفي لإعلان الفئة،
                    // والتفصيل الكامل ينتقل إلى التعليق عند المرور/الضغط.
                    const point = c.track[i];
                    const offset = trackHourOffset(point.time);
                    const visible = offset === null || Math.abs(offset) < 0.01
                        ? true
                        : offset < 0 ? displayRef.current.past : displayRef.current.future;
                    if (!visible) continue;
                    const tColor = categoryColor(point.cat);
                    const dot = document.createElement('div');
                    dot.className = 'cyc-track-dot';
                    // نقطة المسار صارت أيقونة Figma مصغّرة لا قرصاً ملوّناً: نفس رمز
                    // المركز بحجم أصغر، فيُقرأ المسار كسلسلة أطوار للعاصفة نفسها.
                    dot.style.cssText = `position:absolute;--dot-color:${tColor};transform:translate(-50%,-50%);pointer-events:auto;`;
                    dot.innerHTML = displayRef.current.pointStyle === 'dot'
                        ? '<span class="cyc-track-dot-simple" aria-hidden="true"></span>'
                        : `<img class="cyc-track-dot-img" src="${categoryIcon(point.cat)}" alt="" aria-hidden="true" draggable="false" />`;
                    attachTip(dot, c, point, tColor);
                    dot.addEventListener('click', onClick(c.id, i));
                    host.appendChild(dot);
                    markersRef.current.push({ el: dot, lon: point.lon, lat: point.lat });
                }
            }
            position(true);
        };

        rebuildRef.current = rebuild;
        /*
         * عبر الحارس لا حوله. هذا المرجع يستدعيه مؤثّرٌ يعمل مع **كل** تغيّر في زمن
         * الإطار — عدّة مرّات في كل خطوة تشغيل — وكان يمرّر `true` فيتجاوز الحارس
         * كلّه: تكميم الزمن ببكسل (راجع position) لم يُطبَّق أثناء التشغيل قطّ،
         * لأن هذا المسار يلتفّ عليه. تتبّع 2026-09-21: 1770 مللي من هذا المسار وحده.
         * والحارس يتضمّن الزمن المكمَّم، فالعلامة تتحرّك حين يتقدّم الزمن بما يُرى.
         */
        positionRef.current = () => position();
        rebuild();
        const onRender = () => position();
        map.on('render', onRender);

        return () => {
            map.off('render', onRender);
            container.removeEventListener('pointerdown', dismissOnPointerDown, true);
            map.off('dragstart', dismissTooltip);
            map.off('movestart', dismissTooltip);
            map.off('zoomstart', dismissTooltip);
            map.off('rotatestart', dismissTooltip);
            map.off('pitchstart', dismissTooltip);
            rebuildRef.current = null;
            positionRef.current = null;
            overlay.remove();
            // التعليق خارج overlay الآن، فلا يُحذف معه — يُزال صراحةً.
            tooltip.remove();
            tooltipRef.current = null;
        };
    }, [mapRef, reduceMotion]);

    useEffect(() => {
        if (!isVisible) {
            cyclonesRef.current = []; setCyclones([]);
            disturbancesRef.current = []; setDisturbances([]);
            rebuildRef.current?.(); return;
        }
        let cancelled = false;

        const showCyclones = (c: Cyclone[]) => {
            if (cancelled) return;
            cyclonesRef.current = c;
            setCyclones(c);
            rebuildRef.current?.();
        };
        const load = () => {
            // لا نبدأ الترقية قبل أن تُعرض المرحلة الأولى؛ وإلا قد يصل ردّها المتأخر
            // فيطغى على المسارات التفصيلية التي وصلت قبله.
            getInitialActiveCyclones()
                .then(showCyclones)
                .catch(() => undefined)
                .finally(() => {
                    if (cancelled) return;
                    /* التعذّر يُبقي ما هو معروض ولا يمسحه. المسح كان يعني اختفاء
                       الإعصار خمس دقائق كاملة (حتى الدورة التالية) بسبب عثرةٍ
                       واحدة في الشبكة — وبيانات إنذارية لا تُمحى لأنّ طلباً فشل.
                       والخدمة نفسها لا ترمي إلّا حين يسقط المصدران معاً بلا نتيجة
                       محفوظة، فالوصول إلى هنا صار نادراً أصلاً. تقادُم ما يُعرض
                       محدودٌ بنافذة صلاحية آخر نتيجة ناجحة داخل الخدمة. */
                    getActiveCyclones()
                        .then(showCyclones)
                        .catch(() => undefined);
                }); // لا أعاصير / تعذّر الجلب — ليست حالة خطأ
            // طلب مستقلّ: تعذّر جلب الاضطرابات لا يُسقط الأعاصير ولا العكس.
            // `null` = تعذّر الجلب فنُبقي المعروض؛ `[]` = لا اضطرابات فعلاً فنُفرّغ.
            getDisturbances()
                .then(d => {
                    if (cancelled || d === null) return;
                    disturbancesRef.current = d; setDisturbances(d); rebuildRef.current?.();
                })
                .catch(() => undefined);
        };

        load();

        /* ── تحديث دوري ─────────────────────────────────────────────────────────
           بدونه تبقى نشرة قديمة معروضة ما دامت الصفحة مفتوحة. وقع ذلك فعلاً: لسان
           تُرك مفتوحاً عبر تحديثٍ للخادم ظلّ يعرض مخروطاً مقتطعاً بينما الخادم يخدم
           الصحيح — وبيانات إنذارية لا يجوز أن تتقادم صامتةً.

           الإيقاع مأخوذ من Zoom Earth: طبقاتها الحدثية (حرائق/عواصف) تُحدَّث كل
           خمس دقائق (setInterval …, 3e5) بينما البلاطات السريعة كل 10–60 ثانية.
           خمس دقائق أسرع بكثير من نشرة NHC (كل ست ساعات) لكنها تلتقط النشرة
           الاستثنائية خلال دقائق من صدورها، وكلفتها طلبان صغيران.

           الحارس `document.hidden` من عندهم أيضاً وهو الأهمّ: لا جلب واللسان في
           الخلفية. بدونه تستهلك ألسنة منسيّة بيانات المستخدم وبطاريته وتُحمّل الخادم
           بلا مستفيد. ونُحدّث فور العودة إلى اللسان كي لا ينتظر المستخدم دورةً كاملة
           بعد غياب طويل. */
        const REFRESH_MS = 5 * 60 * 1000;
        const tick = () => { if (!document.hidden) load(); };
        const id = window.setInterval(tick, REFRESH_MS);

        // العودة إلى اللسان بعد إخفائه ⇒ ما يُعرض قد يكون قديماً بعمر الغياب كلّه.
        const onVisible = () => { if (!document.hidden) load(); };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            cancelled = true;
            window.clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [isVisible]);

    // أُغلق المرسى من الخارج (فتح المستخدم لوحة أخرى فأقصته منظومة الإقصاء المتبادل):
    // نُنسي الاختيار كي يعود عند إعادة الفتح إلى قائمة الأنظمة لا إلى تفاصيل إعصار قديم.
    useEffect(() => {
        if (!dockOpen) setSelected(null);
    }, [dockOpen]);

    // إخفاء طبقة الأعاصير كلياً يُغلق مرساها — وإلّا بقيت لوحة لطبقة غير معروضة.
    useEffect(() => {
        if (!isVisible && dockOpen) setDockOpen(false);
    }, [isVisible, dockOpen, setDockOpen]);

    const dirKey = selected ? compassKey(selected.movement.dir) : 'N';
    // كروم اللوحة بلون الموقع الثابت لا بلون الفئة: طيف سافير-سيمبسون يبقى محصوراً في
    // البيانات (النقاط والمسار والشارات) فلا يغزو الأحمر/الأصفر واجهةَ التطبيق.
    const detailColor = DOCK_ACCENT;

    // الاضطرابات وحدها تكفي لعرض اللوحة: قد لا يوجد إعصار نشط بينما ترصد النشرة
    // مناطق تطوّر محتمل — وهي الحالة الأغلب خارج ذروة الموسم. الشرط السابق
    // (أعاصير فقط) كان يُخفي اللوحة كلّها فتبقى العلامات على الخريطة بلا قائمة.
    if (!isVisible || (cyclones.length === 0 && disturbances.length === 0)) return null;

    /* ── مرسى واحد بدل مكوّنين عائمين ──────────────────────────────────────────
       التصميم السابق كان قطعتين مستقلّتين (زر "الأنظمة النشطة" + بطاقة التفاصيل) كلٌّ
       يضع نفسه يسار الشاشة — وهي منطقة مشغولة أصلاً بالشعار والشريط الرأسي ولوحة الطقس،
       فكانت الثلاثة تتراكب فعلياً. الحلّ بنيوي لا تجميلي: لوحة واحدة على الحافّة اليمنى
       (الجهة الوحيدة غير المزدحمة) بحالتين — قائمة أو تفاصيل — فيستحيل التراكب لأنه لا
       يوجد سوى عنصر واحد أصلاً. */
    return (
        <div
            className={`cyc-dock${dockOpen ? ' open' : ''}`}
            style={{ ['--cyc-accent' as string]: detailColor }}
        >
            {!dockOpen && (
                <button
                    className="cyc-dock-tab"
                    onClick={() => setDockOpen(true)}
                    aria-expanded={false}
                    // على الجوّال يصير زرّاً دائرياً بأيقونة وشارة فيُخفى النصّ — الوصف يبقى
                    // لقارئ الشاشة وتلميح المرور.
                    aria-label={`${t('cyclone.activeSystems')}: ${cyclones.length}`}
                    title={t('cyclone.activeSystems')}
                >
                    {/* الشكل من CSS (قناع whirlwind) — نفس دوّامة علامات الخريطة، بيضاء. */}
                    <span className="cyc-dock-tab-icon" aria-hidden />

                    <span className="cyc-dock-tab-count">{cyclones.length}</span>
                    <span className="cyc-dock-tab-label">{t('cyclone.activeSystems')}</span>
                </button>
            )}

            {dockOpen && (
                <div className="cyc-dock-panel">
                    <header className="cyc-dock-head">
                        <div className="cyc-dock-heading">
                            {selected && (
                                <button
                                    className="cyc-dock-back"
                                    onClick={() => setSelected(null)}
                                    aria-label={t('cyclone.activeSystems')}
                                    title={t('cyclone.activeSystems')}
                                ><FiChevronRight /></button>
                            )}
                            <span className="cyc-dock-title">
                                {selected ? selected.nameAr : t('cyclone.activeSystems')}
                            </span>
                        </div>
                        <div className="cyc-tabs" role="tablist">
                            <button
                                className={`cyc-tab${tab === 'tracking' ? ' is-on' : ''}`}
                                onClick={() => setTab('tracking')}
                                role="tab"
                                aria-selected={tab === 'tracking'}
                            >{t('cyclone.tabTracking')}</button>
                            <button
                                className={`cyc-tab${tab === 'legend' ? ' is-on' : ''}`}
                                onClick={() => setTab('legend')}
                                role="tab"
                                aria-selected={tab === 'legend'}
                            >{t('cyclone.tabLegend')}</button>
                        </div>
                        <button
                            className="cyc-dock-close"
                            onClick={() => { setSelected(null); setDockOpen(false); }}
                            aria-label={t('cyclone.close')}
                        >×</button>
                    </header>

                    {/* `has-track-detail` يُثبّت الجسم ويترك التمرير لجدول المسار وحده. وهو صحيح في
                        تبويب التتبّع فقط: تبويب دليل الرموز لا جدول فيه، فلو ورث الصنف من مجرّد
                        كون إعصارٍ مختاراً لقُصّ الدليل بلا ممرّر. الشرط تبويبٌ **و**اختيار. */}
                    <div className={`cyc-dock-body${selected && tab === 'tracking' ? ' has-track-detail' : ''}`}>
                        {tab === 'tracking' && !selected && cyclones.map((c) => {
                            const cColor = categoryColor(c.category);
                            return (
                                <button
                                    key={c.id}
                                    className="cyc-row"
                                    style={{ ['--cyc-item-color' as string]: cColor }}
                                    onClick={() => {
                                        setSelected(c);
                                        // عاصفة جديدة ⇒ لا تحديد موروث من سابقتها؛ يعود المرجع
                                        // إلى لحظة "الآن" حتى ينقر المستخدم نقطة بعينها.
                                        setActivePoint(-1);
                                        mapRef.current?.flyTo({ center: [c.lon, c.lat], zoom: 5, duration: 900 });
                                    }}
                                >
                                    {/* صفّ Figma (`Tropical System`) = أيقونة + نصّ، لا قرص مرقّم.
                                        الرقم لم يُفقد: يبقى في السطر الفرعي تحت الاسم مكتوباً
                                        بالكلمات ("الفئة 3") وهو أوضح من رقم عارٍ داخل قرص. */}
                                    <span className="cyc-row-badge">
                                        <img src={categoryIcon(c.category)} alt="" aria-hidden="true" draggable={false} />
                                    </span>
                                    <span className="cyc-row-copy">
                                        <span className="cyc-row-name">{c.nameAr}</span>
                                        <span className="cyc-row-sub">
                                            {t(levelNameKey(c.category))}
                                        </span>
                                    </span>
                                    <span className="cyc-row-wind">{c.maxWind}<span className="cyc-unit">km/h</span></span>
                                </button>
                            );
                        })}

                        {/* الاضطرابات أسفل الأنظمة النشطة لا مختلطةً بها: هي ما *قد*
                            يصير نظاماً، فوضعها في نفس القائمة يوهم بأنها أعاصير قائمة.
                            تظهر في عرض القائمة فقط — لا تفاصيل لها تُفتح. */}
                        {tab === 'tracking' && !selected && disturbances.length > 0 && (
                            <div className="cyc-dist-list">
                                <span className="cyc-legend-head">{t('cyclone.disturbances')}</span>
                                {disturbances.map((d) => (
                                    <button
                                        key={d.id}
                                        className="cyc-row cyc-dist-row"
                                        onClick={() => mapRef.current?.flyTo({ center: [d.lon, d.lat], zoom: 4, duration: 900 })}
                                    >
                                        <span className="cyc-row-badge">
                                            <img src={levelIcon('disturbance')} alt="" aria-hidden="true" draggable={false} />
                                        </span>
                                        <span className="cyc-row-copy">
                                            <span className="cyc-row-name">
                                                {t('cyclone.distArea', { basin: t(`cyclone.basin.${d.basin}`) || d.basin, n: d.area })}
                                            </span>
                                            <span className="cyc-row-sub">
                                                {t('cyclone.distProb', { p2: d.prob2day, p7: d.prob7day })}
                                            </span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {tab === 'legend' && (
                            <div className="cyc-legend">
                                {/* درجات الشدّة بترتيب تنازلي — الأخطر أعلى، كما في مفتاح
                                    Windy وNHC. كل صفّ: الأيقونة نفسها التي تظهر على الخريطة
                                    (لا رمز بديل)، اسم الدرجة، ومدى الرياح الذي يعرّفها. */}
                                <span className="cyc-legend-head">{t('cyclone.legendStrength')}</span>
                                {CYC_LEVELS.map((lv) => (
                                    <div className="cyc-legend-row" key={lv.key}>
                                        <img className="cyc-legend-icon" src={levelIcon(lv.key)} alt="" aria-hidden="true" draggable={false} />
                                        <span className="cyc-legend-name">{t(`cyclone.level.${lv.key}`)}</span>
                                        <span className="cyc-legend-meterwrap">
                                            <span className="cyc-legend-range">
                                                {lv.minWind === null
                                                    ? '—'
                                                    : lv.maxWind === null
                                                        ? `≥ ${lv.minWind}`
                                                        : `${lv.minWind}–${lv.maxWind}`}
                                                {lv.minWind !== null && <span className="cyc-unit">km/h</span>}
                                            </span>
                                            {/* مقياس بصري للشدّة: طوله يمثّل موقع الدرجة على السلّم
                                                كاملاً، فتُقارَن الدرجات بلمحة بدل قراءة سبعة أرقام.
                                                الدرجات بلا رياح معرَّفة (اضطراب/ما بعد مداري/بقايا)
                                                لا شريط لها — شريطٌ فارغ يوحي بقيمة صفر وهي ليست كذلك. */}
                                            {lv.minWind !== null && (
                                                <span className="cyc-legend-meter" aria-hidden="true">
                                                    <span
                                                        className="cyc-legend-meter-fill"
                                                        style={{
                                                            width: lv.maxWind === null ? '100%' : `${Math.round(Math.min(1, lv.maxWind / CYC_SCALE_MAX) * 100)}%`,
                                                            background: lv.color,
                                                        }}
                                                    />
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                ))}

                                <span className="cyc-legend-head">{t('cyclone.legendSymbols')}</span>
                                <div className="cyc-legend-row">
                                    <span className="cyc-legend-swatch cyc-swatch-solid" aria-hidden="true" />
                                    <span className="cyc-legend-name">{t('cyclone.legendSolid')}</span>
                                </div>
                                <div className="cyc-legend-row">
                                    <span className="cyc-legend-swatch cyc-swatch-dashed" aria-hidden="true" />
                                    <span className="cyc-legend-name">{t('cyclone.legendDashed')}</span>
                                </div>
                                <div className="cyc-legend-row">
                                    <span className="cyc-legend-swatch cyc-swatch-cone" aria-hidden="true" />
                                    <span className="cyc-legend-name">{t('cyclone.legendCone')}</span>
                                </div>
                                <div className="cyc-legend-row">
                                    <span className="cyc-legend-swatch cyc-swatch-dots" aria-hidden="true">
                                        <img src={levelIcon('cat-1')} alt="" />
                                        <img src={levelIcon('cat-4')} alt="" />
                                    </span>
                                    <span className="cyc-legend-name">{t('cyclone.legendDots')}</span>
                                </div>
                            </div>
                        )}

                        {tab === 'tracking' && selected && <>
                            {selected.track.length > 1 && (
                                <div className="cyc-track">
                                    <div className="cyc-table-wrap" ref={stripRef}>
                                        <table className="cyc-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('cyclone.colDate')}</th>
                                                    <th>{t('cyclone.colTime')}</th>
                                                    <th>{t('cyclone.colCat')}</th>
                                                    <th>{t('cyclone.colWind')}</th>
                                                    <th>{t('cyclone.colPressure')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                        {trackRows.map(({ point, i }) => {
                                            const absolute = point.time === 'now'
                                                ? new Date()
                                                : new Date(point.time);
                                            const valid = Number.isFinite(absolute.getTime());
                                            const offset = trackHourOffset(point.time);
                                            // "الآن" = النقطة الأقرب للحظة الحالية؛ تُبرَز كصفّ نشط
                                            // تماماً كسطر Zoom Earth المضيء.
                                            const isNow = point.time === 'now'
                                                || (offset !== null && Math.abs(offset) < 1);
                                            // تحديد صريح من المستخدم يتقدّم على "الآن": ما إن
                                            // ينقر نقطة على الخريطة أو صفّاً هنا يصير هو المرجع.
                                            const isActive = activePoint >= 0 ? i === activePoint : isNow;
                                            return (
                                                <tr
                                                    key={`${point.lat}_${point.lon}_${i}`}
                                                    ref={(el) => { if (isActive && el) el.scrollIntoView({ block: 'nearest' }); }}
                                                    className={`cyc-row-pt${isActive ? ' is-active' : ''}${isNow ? ' is-now' : ''}`}
                                                    style={{ ['--cyc-item-color' as string]: categoryColor(point.cat) }}
                                                    tabIndex={0}
                                                    title={`${formatCoord(point.lat, 'N', 'S')} ${formatCoord(point.lon, 'E', 'W')}`}
                                                    // الضغط على توقيت ينقل الخريطة إلى موضعه على المسار
                                                    // بالضبط — الجدول والخريطة يصيران عرضين لشيء واحد.
                                                    onClick={() => {
                                                        selectTrackPoint(selected, i);
                                                    }}
                                                >
                                                    <td className="cyc-td-date">
                                                        {valid
                                                            ? formatTrackDate(absolute, t)
                                                            : '—'}
                                                    </td>
                                                    <td className="cyc-td-time">
                                                        {valid
                                                            ? formatTrackTime(absolute, t)
                                                            : trackTimeLabel(point.time, t('cyclone.now'))}
                                                    </td>
                                                    <td className="cyc-td-cat">
                                                        <span className="cyc-cat-pill">{levelBadge(point.cat)}</span>
                                                    </td>
                                                    <td className="cyc-td-wind">
                                                        {point.wind > 0 ? Math.round(point.wind) : '—'}
                                                    </td>
                                                    {/* نشرة NHC لا تحمل ضغطاً لنقاط التوقّع — الضغط الحالي
                                                        وحده معلوم. نكتبه للصفّ الحالي ونضع علامة توقّع لما
                                                        بعده بدل رقم مُختلَق (Zoom Earth يكتب "تنبؤات جوية"). */}
                                                    <td className="cyc-td-pressure">
                                                        {isNow && selected.pressure
                                                            ? Math.round(selected.pressure)
                                                            : <span className="cyc-forecast-mark">{t('cyclone.forecastMark')}</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* تذييل يلخّص الحالة الراهنة ويُصرّح بالمصدر — بديل مضغوط عن
                                القرص والسلّم وبطاقتَي الإحصاء التي كانت تشغل أوّل شاشة
                                كاملة قبل الجدول. نفس تذييل Zoom Earth: تصنيف + رياح + ضغط. */}
                            <footer className="cyc-summary">
                                <span className="cyc-summary-cat" style={{ background: detailColor }}>
                                    {selected.category >= 1
                                        ? t('cyclone.category', { n: selected.category })
                                        : t(levelNameKey(selected.category))}
                                </span>
                                <span className="cyc-summary-stat">
                                    {Math.round(selected.maxWind)}<span className="cyc-unit">km/h</span>
                                </span>
                                {/* الاتجاه بقي هنا بعد إزالة القرص: السهم يدور بزاوية الحركة
                                    الحقيقية، فتُحفظ المعلومة في سطر واحد بدل كتلة كاملة. */}
                                <span className="cyc-summary-stat cyc-summary-dir">
                                    <WiDirectionUp
                                        className="cyc-summary-arrow"
                                        style={{ transform: `rotate(${selected.movement.dir}deg)` }}
                                    />
                                    {t(`cyclone.compass.${dirKey}`)}
                                </span>
                                {selected.pressure > 0 && (
                                    <span className="cyc-summary-stat">
                                        {Math.round(selected.pressure)}<span className="cyc-unit">hPa</span>
                                    </span>
                                )}
                                <span className="cyc-summary-source" title={t('cyclone.source')}>
                                    {trackSource(selected.id)}
                                </span>
                            </footer>
                        </>}
                    </div>
                </div>
            )}
        </div>
    );
});
